/**
 * Topographie animée réutilisable
 *
 * Markup attendu :
 *
 * <div class="topo-canvas topo-canvas--home js-topo-canvas pos-absolute inset-0 z-0 pe-none" aria-hidden="true">
 *   <canvas class="topo-canvas__canvas"></canvas>
 * </div>
 */

document.addEventListener('DOMContentLoaded', () => {
  const topoHosts = document.querySelectorAll('.js-topo-canvas');

  if (!topoHosts.length) {
    return;
  }

  topoHosts.forEach((host, index) => {
    const canvas = host.querySelector('.topo-canvas__canvas');

    if (!canvas) {
      return;
    }

    initTopoCanvas(host, canvas, index);
  });
});

function initTopoCanvas(host, canvas, index = 0) {
  const config = {
    lineColor: '#3459A5',
    bgColor: 'transparent',
    patternScale: 1.6,
    contourLevels: 7,
    speed: 0.45,
    lineWidth: 1.8,
    opacity: 0.6,
    motion: 'flux+souris',
    indexContours: true,
    animateMobile: true,
  };

  let ctx = null;
  let width = 0;
  let height = 0;
  let dpr = 1;
  let raf = null;
  let values = null;
  let z = index * 0.75;
  let panX = index * 140;
  let last = 0;
  let isRunning = false;
  let isVisible = true;

  const mouse = {
    x: -99999,
    y: -99999,
    active: false,
  };

  const noise = makeNoise(20259 + index * 31);

  const resize = () => {
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    width = host.clientWidth;
    height = host.clientHeight;

    if (!width || !height) {
      return;
    }

    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    ctx = canvas.getContext('2d');
  };

  const updateMousePosition = (event) => {
    const pointer = event.touches ? event.touches[0] : event;
    const rect = host.getBoundingClientRect();

    mouse.x = pointer.clientX - rect.left;
    mouse.y = pointer.clientY - rect.top;
    mouse.active = true;
  };

  const resetMousePosition = () => {
    mouse.x = -99999;
    mouse.y = -99999;
    mouse.active = false;
  };

  const loop = (now) => {
    if (!isRunning || !isVisible) {
      return;
    }

    const deltaTime = Math.min((now - last) / 1000, 0.05);

    last = now;

    drawTopo(deltaTime);

    raf = requestAnimationFrame(loop);
  };

  const start = () => {
    if (isRunning) {
      return;
    }

    resize();

    if (!ctx || !width || !height) {
      return;
    }

    isRunning = true;
    last = performance.now();
    raf = requestAnimationFrame(loop);
  };

  const stop = () => {
    isRunning = false;

    if (raf) {
      cancelAnimationFrame(raf);
      raf = null;
    }
  };

  const drawTopo = (deltaTime) => {
    if (!ctx || !width || !height) {
      return;
    }

    const mobileStatic = !config.animateMobile && width < 768;
    const motion = mobileStatic ? 'none' : config.motion;
    const useMouse = motion === 'souris' || motion === 'flux+souris';
    const useFlux = motion === 'flux' || motion === 'flux+souris';

    if (useFlux) {
      z += deltaTime * config.speed * 0.16;
      panX += deltaTime * config.speed * 7;
    }

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    if (config.bgColor === 'transparent') {
      ctx.clearRect(0, 0, width, height);
    } else {
      ctx.fillStyle = config.bgColor;
      ctx.fillRect(0, 0, width, height);
    }

    const cell = 14;
    const zoom = Math.max(0.4, config.patternScale);
    const cols = Math.ceil(width / cell);
    const rows = Math.ceil(height / cell);
    const nc = cols + 1;
    const nr = rows + 1;

    if (!values || values.length !== nc * nr) {
      values = new Float32Array(nc * nr);
    }

    const scale = 0.0017 / zoom;
    const mouseActive = useMouse && mouse.active;
    const mx = mouse.x;
    const my = mouse.y;
    const mouseRadius = 2 * 240 * 240;

    for (let row = 0; row < nr; row++) {
      const py = row * cell;
      const sy = py * scale;

      for (let col = 0; col < nc; col++) {
        const px = col * cell;
        const sx = (px + panX) * scale;

        let value = noise(sx, sy, z) + 0.5 * noise(sx * 2 + 100, sy * 2 + 100, z * 1.3 + 50);

        value /= 1.5;

        if (mouseActive) {
          const dx = px - mx;
          const dy = py - my;

          value += 0.9 * Math.exp(-(dx * dx + dy * dy) / mouseRadius);
        }

        values[row * nc + col] = value;
      }
    }

    const low = -0.82;
    const high = 0.82;

    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.strokeStyle = config.lineColor;

    for (let level = 0; level < config.contourLevels; level++) {
      const threshold = low + (level + 0.5) * (high - low) / config.contourLevels;
      const isIndexContour = config.indexContours && level % 3 === 0;
      const segments = [];

      for (let row = 0; row < rows; row++) {
        const y0 = row * cell;
        const y1 = y0 + cell;
        const row0 = row * nc;
        const row1 = (row + 1) * nc;

        for (let col = 0; col < cols; col++) {
          const x0 = col * cell;
          const x1 = x0 + cell;

          const tl = values[row0 + col];
          const tr = values[row0 + col + 1];
          const br = values[row1 + col + 1];
          const bl = values[row1 + col];

          let id = 0;

          if (tl > threshold) {
            id |= 8;
          }

          if (tr > threshold) {
            id |= 4;
          }

          if (br > threshold) {
            id |= 2;
          }

          if (bl > threshold) {
            id |= 1;
          }

          if (id === 0 || id === 15) {
            continue;
          }

          const top = interpolate(x0, y0, x1, y0, tl, tr, threshold);
          const right = interpolate(x1, y0, x1, y1, tr, br, threshold);
          const bottom = interpolate(x0, y1, x1, y1, bl, br, threshold);
          const left = interpolate(x0, y0, x0, y1, tl, bl, threshold);

          switch (id) {
            case 1:
              segments.push(bottom.x, bottom.y, left.x, left.y);
              break;
            case 2:
              segments.push(right.x, right.y, bottom.x, bottom.y);
              break;
            case 3:
              segments.push(right.x, right.y, left.x, left.y);
              break;
            case 4:
              segments.push(top.x, top.y, right.x, right.y);
              break;
            case 5:
              segments.push(top.x, top.y, left.x, left.y, right.x, right.y, bottom.x, bottom.y);
              break;
            case 6:
              segments.push(top.x, top.y, bottom.x, bottom.y);
              break;
            case 7:
              segments.push(top.x, top.y, left.x, left.y);
              break;
            case 8:
              segments.push(top.x, top.y, left.x, left.y);
              break;
            case 9:
              segments.push(top.x, top.y, bottom.x, bottom.y);
              break;
            case 10:
              segments.push(top.x, top.y, right.x, right.y, bottom.x, bottom.y, left.x, left.y);
              break;
            case 11:
              segments.push(top.x, top.y, right.x, right.y);
              break;
            case 12:
              segments.push(right.x, right.y, left.x, left.y);
              break;
            case 13:
              segments.push(right.x, right.y, bottom.x, bottom.y);
              break;
            case 14:
              segments.push(bottom.x, bottom.y, left.x, left.y);
              break;
            default:
              break;
          }
        }
      }

      ctx.lineWidth = isIndexContour ? config.lineWidth * 1.7 : config.lineWidth;
      ctx.globalAlpha = Math.min(1, config.opacity * (isIndexContour ? 1 : 0.66));
      ctx.beginPath();

      const paths = buildChains(segments);

      paths.forEach((path) => {
        smoothPath(ctx, path.points, path.closed);
      });

      ctx.stroke();
    }

    ctx.globalAlpha = 1;
  };

  resize();

  window.addEventListener('resize', resize);
  host.addEventListener('mousemove', updateMousePosition);
  host.addEventListener('mouseleave', resetMousePosition);
  host.addEventListener('touchmove', updateMousePosition, { passive: true });
  host.addEventListener('touchend', resetMousePosition);

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        isVisible = entry.isIntersecting;

        if (isVisible) {
          start();
        } else {
          stop();
        }
      });
    },
    {
      threshold: 0.05,
    }
  );

  observer.observe(host);

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      stop();
      return;
    }

    if (isVisible) {
      start();
    }
  });
}

function interpolate(x1, y1, x2, y2, value1, value2, threshold) {
  const diff = value2 - value1;
  const ratio = Math.abs(diff) < 0.00001 ? 0.5 : (threshold - value1) / diff;

  return {
    x: x1 + (x2 - x1) * ratio,
    y: y1 + (y2 - y1) * ratio,
  };
}

function buildChains(segments) {
  const precision = 2;
  const nodes = new Map();
  const used = [];

  function getNode(x, y) {
    const key = `${(x * precision) | 0},${(y * precision) | 0}`;
    let node = nodes.get(key);

    if (!node) {
      node = {
        x,
        y,
        neighbours: [],
      };

      nodes.set(key, node);
    }

    return node;
  }

  for (let i = 0; i < segments.length; i += 4) {
    const a = getNode(segments[i], segments[i + 1]);
    const b = getNode(segments[i + 2], segments[i + 3]);

    if (a === b) {
      continue;
    }

    const edgeIndex = used.length;

    used.push(false);
    a.neighbours.push({ to: b, edgeIndex });
    b.neighbours.push({ to: a, edgeIndex });
  }

  const paths = [];

  function walk(start) {
    const points = [[start.x, start.y]];
    let current = start;

    while (true) {
      let next = null;
      let nextEdgeIndex = -1;
      const neighbours = current.neighbours;

      for (let i = 0; i < neighbours.length; i++) {
        if (!used[neighbours[i].edgeIndex]) {
          next = neighbours[i].to;
          nextEdgeIndex = neighbours[i].edgeIndex;
          break;
        }
      }

      if (!next) {
        break;
      }

      used[nextEdgeIndex] = true;
      points.push([next.x, next.y]);
      current = next;

      if (current === start) {
        break;
      }
    }

    return points;
  }

  nodes.forEach((node) => {
    if (node.neighbours.length === 1 && !used[node.neighbours[0].edgeIndex]) {
      paths.push({
        points: walk(node),
        closed: false,
      });
    }
  });

  nodes.forEach((node) => {
    for (let i = 0; i < node.neighbours.length; i++) {
      if (!used[node.neighbours[i].edgeIndex]) {
        const points = walk(node);

        paths.push({
          points,
          closed: points.length > 2,
        });

        break;
      }
    }
  });

  return paths;
}

function smoothPath(ctx, points, closed) {
  const count = points.length;

  if (count < 2) {
    return;
  }

  if (count === 2) {
    ctx.moveTo(points[0][0], points[0][1]);
    ctx.lineTo(points[1][0], points[1][1]);
    return;
  }

  if (closed) {
    ctx.moveTo(
      (points[count - 1][0] + points[0][0]) / 2,
      (points[count - 1][1] + points[0][1]) / 2
    );

    for (let i = 0; i < count; i++) {
      const current = points[i];
      const next = points[(i + 1) % count];

      ctx.quadraticCurveTo(
        current[0],
        current[1],
        (current[0] + next[0]) / 2,
        (current[1] + next[1]) / 2
      );
    }

    return;
  }

  ctx.moveTo(points[0][0], points[0][1]);

  for (let i = 1; i < count - 1; i++) {
    ctx.quadraticCurveTo(
      points[i][0],
      points[i][1],
      (points[i][0] + points[i + 1][0]) / 2,
      (points[i][1] + points[i + 1][1]) / 2
    );
  }

  ctx.lineTo(points[count - 1][0], points[count - 1][1]);
}

function makeNoise(seed) {
  const p = new Uint8Array(256);

  for (let i = 0; i < 256; i++) {
    p[i] = i;
  }

  let s = seed || 1234;

  function random() {
    s = (s * 16807) % 2147483647;
    return s / 2147483647;
  }

  for (let i = 255; i > 0; i--) {
    const randomIndex = Math.floor(random() * (i + 1));
    const temp = p[i];

    p[i] = p[randomIndex];
    p[randomIndex] = temp;
  }

  const perm = new Uint8Array(512);
  const pm = new Uint8Array(512);

  for (let i = 0; i < 512; i++) {
    perm[i] = p[i & 255];
    pm[i] = perm[i] % 12;
  }

  const gradients = [
    [1, 1, 0],
    [-1, 1, 0],
    [1, -1, 0],
    [-1, -1, 0],
    [1, 0, 1],
    [-1, 0, 1],
    [1, 0, -1],
    [-1, 0, -1],
    [0, 1, 1],
    [0, -1, 1],
    [0, 1, -1],
    [0, -1, -1],
  ];

  const f3 = 1 / 3;
  const g3 = 1 / 6;

  return function simplexNoise(xin, yin, zin) {
    let n0;
    let n1;
    let n2;
    let n3;

    const skew = (xin + yin + zin) * f3;
    const i = Math.floor(xin + skew);
    const j = Math.floor(yin + skew);
    const k = Math.floor(zin + skew);
    const unskew = (i + j + k) * g3;

    const x0 = xin - (i - unskew);
    const y0 = yin - (j - unskew);
    const z0 = zin - (k - unskew);

    let i1;
    let j1;
    let k1;
    let i2;
    let j2;
    let k2;

    if (x0 >= y0) {
      if (y0 >= z0) {
        i1 = 1;
        j1 = 0;
        k1 = 0;
        i2 = 1;
        j2 = 1;
        k2 = 0;
      } else if (x0 >= z0) {
        i1 = 1;
        j1 = 0;
        k1 = 0;
        i2 = 1;
        j2 = 0;
        k2 = 1;
      } else {
        i1 = 0;
        j1 = 0;
        k1 = 1;
        i2 = 1;
        j2 = 0;
        k2 = 1;
      }
    } else if (y0 < z0) {
      i1 = 0;
      j1 = 0;
      k1 = 1;
      i2 = 0;
      j2 = 1;
      k2 = 1;
    } else if (x0 < z0) {
      i1 = 0;
      j1 = 1;
      k1 = 0;
      i2 = 0;
      j2 = 1;
      k2 = 1;
    } else {
      i1 = 0;
      j1 = 1;
      k1 = 0;
      i2 = 1;
      j2 = 1;
      k2 = 0;
    }

    const x1 = x0 - i1 + g3;
    const y1 = y0 - j1 + g3;
    const z1 = z0 - k1 + g3;
    const x2 = x0 - i2 + 2 * g3;
    const y2 = y0 - j2 + 2 * g3;
    const z2 = z0 - k2 + 2 * g3;
    const x3 = x0 - 1 + 3 * g3;
    const y3 = y0 - 1 + 3 * g3;
    const z3 = z0 - 1 + 3 * g3;

    const ii = i & 255;
    const jj = j & 255;
    const kk = k & 255;

    let t;
    let gradient;

    t = 0.6 - x0 * x0 - y0 * y0 - z0 * z0;

    if (t < 0) {
      n0 = 0;
    } else {
      t *= t;
      gradient = gradients[pm[ii + perm[jj + perm[kk]]]];
      n0 = t * t * (gradient[0] * x0 + gradient[1] * y0 + gradient[2] * z0);
    }

    t = 0.6 - x1 * x1 - y1 * y1 - z1 * z1;

    if (t < 0) {
      n1 = 0;
    } else {
      t *= t;
      gradient = gradients[pm[ii + i1 + perm[jj + j1 + perm[kk + k1]]]];
      n1 = t * t * (gradient[0] * x1 + gradient[1] * y1 + gradient[2] * z1);
    }

    t = 0.6 - x2 * x2 - y2 * y2 - z2 * z2;

    if (t < 0) {
      n2 = 0;
    } else {
      t *= t;
      gradient = gradients[pm[ii + i2 + perm[jj + j2 + perm[kk + k2]]]];
      n2 = t * t * (gradient[0] * x2 + gradient[1] * y2 + gradient[2] * z2);
    }

    t = 0.6 - x3 * x3 - y3 * y3 - z3 * z3;

    if (t < 0) {
      n3 = 0;
    } else {
      t *= t;
      gradient = gradients[pm[ii + 1 + perm[jj + 1 + perm[kk + 1]]]];
      n3 = t * t * (gradient[0] * x3 + gradient[1] * y3 + gradient[2] * z3);
    }

    return 32 * (n0 + n1 + n2 + n3);
  };
}
